/**
 * Map tile sources, styles, and themes.
 *
 * Each "source" is a tile provider (OSM, Google, Carto, etc.).
 * Each source offers one or more "styles" (streets, satellite, terrain, dark).
 * The app stores the user's preferred source+style in localStorage and
 * auto-selects a dark variant when the app is in dark mode (if available).
 */

export interface MapStyle {
  id: string
  label: string
  url: string
  attribution: string
  maxZoom?: number
  /** If true, this style is used automatically in dark mode */
  dark?: boolean
  preview?: string
}

export interface MapSource {
  id: string
  label: string
  styles: MapStyle[]
}

// ── Tile Sources ──────────────────────────────────────────────────

export const MAP_SOURCES: MapSource[] = [
  {
    id: 'osm',
    label: 'OpenStreetMap',
    styles: [
      {
        id: 'streets',
        label: 'Streets',
        url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19,
      },
    ],
  },
  {
    id: 'carto',
    label: 'Carto',
    styles: [
      {
        id: 'light',
        label: 'Light',
        url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>',
        maxZoom: 20,
      },
      {
        id: 'dark',
        label: 'Dark',
        url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>',
        maxZoom: 20,
        dark: true,
      },
    ],
  },
  {
    id: 'google',
    label: 'Google',
    styles: [
      {
        id: 'streets',
        label: 'Streets',
        url: 'https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}',
        attribution: '&copy; Google',
        maxZoom: 20,
      },
      {
        id: 'satellite',
        label: 'Satellite',
        url: 'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
        attribution: '&copy; Google',
        maxZoom: 20,
        dark: true,
      },
      {
        id: 'hybrid',
        label: 'Hybrid',
        url: 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
        attribution: '&copy; Google',
        maxZoom: 20,
      },
      {
        id: 'terrain',
        label: 'Terrain',
        url: 'https://mt1.google.com/vt/lyrs=p&x={x}&y={y}&z={z}',
        attribution: '&copy; Google',
        maxZoom: 20,
      },
    ],
  },
  {
    id: 'esri',
    label: 'Esri',
    styles: [
      {
        id: 'worldstreet',
        label: 'World Street',
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
        attribution: '&copy; Esri',
        maxZoom: 19,
      },
      {
        id: 'satellite',
        label: 'Satellite',
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        attribution: '&copy; Esri',
        maxZoom: 19,
        dark: true,
      },
      {
        id: 'topo',
        label: 'Topographic',
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}',
        attribution: '&copy; Esri',
        maxZoom: 19,
      },
    ],
  },
]

// ── Helpers ────────────────────────────────────────────────────────

const STORAGE_KEY = 'omnitrace-map-style'

interface SavedMapPref {
  sourceId: string
  styleId: string
}

export function getSavedMapPref(): SavedMapPref {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return JSON.parse(raw)
  } catch { /* ignore */ }
  return { sourceId: 'carto', styleId: 'light' }
}

export function saveMapPref(sourceId: string, styleId: string): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ sourceId, styleId }))
}

/**
 * Resolve the active tile style. If the app is in dark mode and the user's
 * chosen source has a dark variant, use it automatically.
 */
export function resolveMapStyle(darkMode: boolean): MapStyle {
  const { sourceId, styleId } = getSavedMapPref()
  const source = MAP_SOURCES.find((s) => s.id === sourceId) ?? MAP_SOURCES[1] // default: carto

  // If dark mode, prefer the dark variant of the selected source
  if (darkMode) {
    const darkStyle = source.styles.find((s) => s.dark)
    if (darkStyle) return darkStyle
  }

  // Otherwise use the user's selected style
  const style = source.styles.find((s) => s.id === styleId)
  return style ?? source.styles[0]
}

/**
 * Get the current source (for showing which provider is selected in UI).
 */
export function getActiveSource(): MapSource {
  const { sourceId } = getSavedMapPref()
  return MAP_SOURCES.find((s) => s.id === sourceId) ?? MAP_SOURCES[1]
}
